import { API_BASE } from '../../shared/utils/apiConfig';
import i18n from '../../i18n';

const fp = (key, opts) => i18n.t(`setup.forgotPassword.errors.${key}`, opts);
const pr = (key, opts) => i18n.t(`serviceMessages.passwordReset.${key}`, opts);
const profileMsg = (key, opts) => i18n.t(`modules.shared.profile.${key}`, opts);

const LICENSING_API_URL =
  process.env.REACT_APP_LICENSING_API_URL || 'https://qawaem-platform.qawaemaicode.workers.dev';

/**
 * Request a password reset email (local API + Resend via Console worker).
 */
export async function requestPasswordReset(email) {
  const em = String(email || '').trim();
  if (!em) {
    return { success: false, error: fp('enter_email'), code: 'EMAIL_REQUIRED' };
  }

  if (em.includes('@') && !/\S+@\S+\.\S+/.test(em)) {
    return { success: false, error: fp('invalid_email'), code: 'INVALID_EMAIL' };
  }

  try {
    const response = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: em }),
    });
    const payload = await response.json().catch(() => ({}));
    const data = payload.data || payload;
    const apiError = payload.error || data.error;

    if (response.ok && data.success) {
      return {
        success: true,
        message: data.message,
        email: data.email,
        status: response.status,
      };
    }

    return {
      success: false,
      message: apiError,
      error: apiError,
      code: payload.code || data.code,
      status: response.status,
    };
  } catch (error) {
    console.error('Password reset request error:', error);
    return {
      success: false,
      error: fp('network'),
      code: 'NETWORK_ERROR',
    };
  }
}

/**
 * Update admin profile (username for login, email, etc.)
 */
export async function updateAdminProfile({
  currentUsername,
  username,
  email,
  jobTitle,
  phone,
  department,
}) {
  try {
    const response = await fetch(`${API_BASE}/auth/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        currentUsername: currentUsername?.trim() || undefined,
        username: username?.trim(),
        email: email?.trim(),
        jobTitle: jobTitle?.trim() || '',
        phone: phone?.trim() || '',
        department: department?.trim() || '',
      }),
    });
    const payload = await response.json().catch(() => ({}));
    const data = payload.data || payload;
    const apiError = payload.error || data.error;

    if (response.ok && data.success) {
      return {
        success: true,
        message: data.message || profileMsg('msg_profile_updated'),
        user: data.user,
        status: response.status,
      };
    }

    return {
      success: false,
      error: apiError || profileMsg('msg_profile_error'),
      code: payload.code || data.code,
      status: response.status,
    };
  } catch (error) {
    console.error('Profile update error:', error);
    return {
      success: false,
      error: fp('network'),
      code: 'NETWORK_ERROR',
    };
  }
}

/**
 * Change password while logged in (syncs SQLite + settings via local API).
 */
export async function changePasswordWithCurrent({ username, currentPassword, newPassword }) {
  const user = String(username || '').trim();
  if (!user || !currentPassword || !newPassword) {
    return { success: false, error: pr('missing_fields'), code: 'MISSING_FIELDS' };
  }

  try {
    const response = await fetch(`${API_BASE}/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: user, currentPassword, newPassword }),
    });
    const payload = await response.json().catch(() => ({}));
    const data = payload.data || payload;
    const apiError = payload.error || data.error;

    if (response.ok && data.success) {
      return {
        success: true,
        message: data.message || profileMsg('msg_password_updated'),
        status: response.status,
      };
    }

    return {
      success: false,
      error: apiError || profileMsg('msg_password_error'),
      code: payload.code || data.code,
      status: response.status,
    };
  } catch (error) {
    console.error('Change password error:', error);
    return {
      success: false,
      error: fp('network'),
      code: 'NETWORK_ERROR',
    };
  }
}

/**
 * Complete password reset with email + confirmation code.
 */
export async function resetPasswordWithCode({ email, code, newPassword }) {
  if (!email || !code || !newPassword) {
    return { success: false, error: 'MISSING_FIELDS' };
  }

  try {
    const response = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: String(email).trim(), code: String(code).trim(), newPassword }),
    });
    const payload = await response.json().catch(() => ({}));
    const data = payload.data || payload;
    return {
      success: response.ok && data.success,
      message: data.message,
      error: payload.error || data.error,
      code: payload.code || data.code,
      status: response.status,
    };
  } catch (error) {
    console.error('Password reset error:', error);
    return { success: false, error: 'NETWORK_ERROR' };
  }
}

/**
 * @deprecated Legacy token link — prefer resetPasswordWithCode
 */
export async function resetPasswordWithToken({ token, newPassword }) {
  if (!token || !newPassword) {
    return { success: false, error: 'MISSING_FIELDS' };
  }

  try {
    const response = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    const payload = await response.json().catch(() => ({}));
    const data = payload.data || payload;
    return {
      success: response.ok && data.success,
      message: data.message,
      error: payload.error || data.error,
      status: response.status,
    };
  } catch (error) {
    console.error('Password reset error:', error);
    return { success: false, error: 'NETWORK_ERROR' };
  }
}

/**
 * Validate reset token before showing reset form.
 */
export async function validateResetToken(token) {
  if (!token) return { valid: false };
  try {
    const response = await fetch(
      `${API_BASE}/auth/validate-reset-token?token=${encodeURIComponent(token)}`,
    );
    const payload = await response.json().catch(() => ({}));
    const data = payload.data || payload;
    return { valid: response.ok && data.valid === true, username: data.username };
  } catch {
    return { valid: false };
  }
}

export { LICENSING_API_URL };
